// HSPP-SPECIFIC: Import OTP_AUTH_CONFIG instead of API_CONFIG
import { OTP_AUTH_CONFIG } from '../config/api';
import { apiInterceptor } from './apiInterceptor';

const API_BASE_URL = OTP_AUTH_CONFIG.BASE_URL;
const JWT_STORAGE_KEY = OTP_AUTH_CONFIG.JWT_STORAGE_KEY;

export interface User {
  id: string;
  name: string;
  email: string;
}

export interface UserInfoResponse {
  id: string;
  name: string;
  email: string;
  created_at: string;
}

export interface SignupRequest {
  name: string;
  email: string;
}

export interface SignupResponse {
  jwt?: string;
  user_id?: string;
  message: string;
  requires_otp?: boolean;
  is_returning_user?: boolean;
}

export interface OTPVerificationRequest {
  email: string;
  otp_code: string;
}

export interface OTPVerificationResponse {
  jwt: string;
  user_id: string;
  message: string;
}

export interface OTPRequestRequest {
  email: string;
}

export interface OTPRequestResponse {
  message: string;
}

export class AuthService {
  private static instance: AuthService;
  private jwt: string | null = null;
  private user: User | null = null;

  private constructor() {
    this.loadTokenFromStorage();
  }

  public static getInstance(): AuthService {
    if (!AuthService.instance) {
      AuthService.instance = new AuthService();
    }
    return AuthService.instance;
  }

  private updateUserFromJwt(jwt: string) {
    try {
      // Support tokens stored with a leading "Bearer " prefix
      const raw = jwt.startsWith('Bearer ') ? jwt.slice(7) : jwt;
      const part = (raw.split('.')[1] || '');
      // Convert base64url to base64 with padding
      const b64 = part.replace(/-/g, '+').replace(/_/g, '/');
      const pad = b64.length % 4 === 2 ? '==': b64.length % 4 === 3 ? '=' : '';
      const json = atob(b64 + pad);
      const payload = JSON.parse(json);
      const email: string | undefined = payload.email || payload.user_email || payload.sub;
      const id: string = payload.user_id || payload.sub || 'unknown';
      const name: string = payload.name || '';
      this.user = email ? { id, name, email } : null;
      if (this.user) {
        try {
          localStorage.setItem('reflection_user', JSON.stringify({ id: this.user.id, email: this.user.email, name: this.user.name }));
          window.dispatchEvent(new Event('reflection_user_updated'));
        } catch {}
      }
    } catch {
      this.user = null;
    }
  }

  private loadUserProfileFromStorage(): void {
    try {
      const raw = localStorage.getItem('reflection_user');
      if (!raw) return;
      const obj = JSON.parse(raw);
      const email: string | undefined = obj.email || obj.user_email;
      const id: string = obj.id || 'local_user';
      const name: string = obj.name || obj.displayName || '';
      if (email) {
        this.user = { id, name, email };
      }
    } catch {
      // ignore
    }
  }

  private loadTokenFromStorage(): void {
    try {
      const storedJwt = localStorage.getItem(JWT_STORAGE_KEY);
      if (storedJwt) {
        this.jwt = storedJwt;
        this.updateUserFromJwt(storedJwt);
        if (!this.user) {
          // Fallback to profile cache if JWT lacks email claim
          this.loadUserProfileFromStorage();
        }
      }
    } catch (error) {
      console.error('Failed to load JWT from storage:', error);
    }
  }

  private saveTokenToStorage(jwt: string): void {
    try {
      localStorage.setItem(JWT_STORAGE_KEY, jwt);
      this.jwt = jwt;
      this.updateUserFromJwt(jwt);
    } catch (error) {
      console.error('Failed to save JWT to storage:', error);
      throw new Error('Failed to save authentication token');
    }
  }

  public getToken(): string | null {
    return this.jwt;
  }

  public isAuthenticated(): boolean {
    return this.jwt !== null;
  }

  public async signup(request: SignupRequest): Promise<SignupResponse> {
    try {
      const response = await apiInterceptor.post(`${API_BASE_URL}/auth/signup`, request);

      const data = await response.json();

      if (response.ok) {
        // Only save token if JWT is provided (new user)
        if (data.jwt) {
          this.saveTokenToStorage(data.jwt);
        }
        return data;
      } else {
        // Enhanced error handling for specific scenarios
        let errorMessage = data.message || 'Signup failed';
        
        if (response.status === 429) {
          errorMessage = 'Too many signup attempts. Please wait before trying again.';
        } else if (response.status === 400) {
          if (data.message?.includes('email')) {
            errorMessage = 'Please enter a valid email address.';
          } else if (data.message?.includes('name')) {
            errorMessage = 'Please enter a valid name.';
          }
        } else if (response.status === 500) {
          errorMessage = 'Server error. Please try again later.';
        }
        
        throw new Error(errorMessage);
      }
    } catch (error) {
      if (error instanceof TypeError && error.message.includes('fetch')) {
        throw new Error('Unable to connect to the server. Please check your internet connection.');
      }
      console.error('Signup error:', error);
      throw error;
    }
  }

  public async verifyOTP(request: OTPVerificationRequest): Promise<OTPVerificationResponse> {
    try {
      const response = await apiInterceptor.post(`${API_BASE_URL}/auth/verify-otp`, request);

      const data = await response.json();

      if (response.ok) {
        this.saveTokenToStorage(data.jwt);
        return data;
      } else {
        // Enhanced error handling for specific scenarios
        let errorMessage = data.message || 'OTP verification failed';
        
        if (response.status === 429) {
          errorMessage = 'Too many attempts. Please wait before trying again.';
        } else if (response.status === 400) {
          if (data.message?.includes('expired')) {
            errorMessage = 'Your verification code has expired. Please request a new one.';
          } else if (data.message?.includes('invalid') || data.message?.includes('incorrect')) {
            errorMessage = 'Invalid verification code. Please check and try again.';
          }
        } else if (response.status === 404) {
          errorMessage = 'No verification code found. Please request a new one.';
        }
        
        throw new Error(errorMessage);
      }
    } catch (error) {
      if (error instanceof TypeError && error.message.includes('fetch')) {
        throw new Error('Unable to connect to the server. Please check your internet connection.');
      }
      console.error('OTP verification error:', error);
      throw error;
    }
  }

  public async requestOTP(request: OTPRequestRequest): Promise<OTPRequestResponse> {
    try {
      const response = await apiInterceptor.post(`${API_BASE_URL}/auth/request-otp`, request);

      const data = await response.json();

      if (response.ok) {
        return data;
      } else {
        // Enhanced error handling for specific scenarios
        let errorMessage = data.message || 'Failed to send verification code';
        
        if (response.status === 429) {
          errorMessage = 'Too many requests. Please wait before requesting another code.';
        } else if (response.status === 404) {
          errorMessage = 'Email address not found. Please sign up first.';
        } else if (response.status === 400) {
          errorMessage = 'Invalid email address. Please check and try again.';
        }
        
        throw new Error(errorMessage);
      }
    } catch (error) {
      if (error instanceof TypeError && error.message.includes('fetch')) {
        throw new Error('Unable to connect to the server. Please check your internet connection.');
      }
      console.error('OTP request error:', error);
      throw error;
    }
  }

  public getAuthHeaders(): Record<string, string> {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };

    if (this.jwt) {
      headers['Authorization'] = `Bearer ${this.jwt}`;
    }

    return headers;
  }

  public logout(): void {
    try {
      localStorage.removeItem(JWT_STORAGE_KEY);
      try { localStorage.removeItem('reflection_user'); } catch {}
      this.jwt = null;
      this.user = null;
      try { window.dispatchEvent(new Event('reflection_user_updated')); } catch {}
    } catch (error) {
      console.error('Failed to logout:', error);
    }
  }

  public getUser(): User | null {
    return this.user;
  }

  public async getUserInfo(): Promise<UserInfoResponse> {
    const response = await this.makeAuthenticatedRequest(`${API_BASE_URL}/auth/user-info`);
    
    if (!response.ok) {
      throw new Error(`Failed to fetch user info: ${response.status}`);
    }
    
    return await response.json();
  }

  // Helper method for making authenticated API calls
  public async makeAuthenticatedRequest(
    url: string,
    options: RequestInit = {}
  ): Promise<Response> {
    const headers = {
      ...this.getAuthHeaders(),
      ...options.headers,
    };

    const res = await apiInterceptor.request(url, {
      ...options,
      headers,
    });
    try {
      if (res.status === 401 || res.status === 409) {
        window.dispatchEvent(new CustomEvent('auth_required', { detail: { status: res.status, url } }));
      }
    } catch {}
    return res;
  }
}

export const authService = AuthService.getInstance();